import { type FC, useState } from 'react';
import Button from '@components/Button';
import InputText from '@components/InputText';
import Modal from '@components/Modal';

interface PublishButtonProps {
  hasUnpublishedChanges: boolean;
  isDraftDirty: boolean;
  isPublishing: boolean;
  onPublish: (changeNote?: string) => Promise<void>;
}

export const PublishButton: FC<PublishButtonProps> = ({
  hasUnpublishedChanges,
  isDraftDirty,
  isPublishing,
  onPublish,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');

  /**
   * Require saving any in-flight draft edits before publishing — the publish
   * endpoint snapshots the last saved draft, so unsaved UI state would be lost.
   */
  const canPublish = hasUnpublishedChanges && !isDraftDirty;

  const tooltip = !hasUnpublishedChanges
    ? 'No unpublished changes in the draft'
    : isDraftDirty
      ? 'Save draft changes before publishing'
      : 'Publish a new version from the current draft';

  const handleConfirm = async () => {
    await onPublish(changeNote.trim() || undefined);
    setChangeNote('');
    setIsOpen(false);
  };

  return (
    <>
      <Button
        kind="primary"
        className="h-9.5"
        onClick={() => setIsOpen(true)}
        isDisabled={!canPublish || isPublishing}
        loading={isPublishing}
        title={tooltip}
        data-testid="button:publish-eval-config"
      >
        Publish version
      </Button>
      {isOpen && (
        <Modal
          className="w-md gap-0"
          open
          onClose={() => setIsOpen(false)}
          title="Publish new version"
          titleClassName="text-lg leading-6 font-medium"
        >
          <div className="flex flex-col gap-4 px-6 pt-2 pb-4">
            <p className="text-info-500 text-sm">
              A new immutable version will be snapshotted from the current draft. Published versions cannot be edited.
            </p>
            <InputText
              labelText="Change note (optional)"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              dataTestId="text:publish-change-note"
            />
            <div className="flex flex-row-reverse gap-3">
              <Button
                kind="primary"
                className="h-9.5"
                onClick={handleConfirm}
                loading={isPublishing}
                isDisabled={isPublishing}
                data-testid="button:confirm-publish"
              >
                Publish
              </Button>
              <Button className="h-9.5" onClick={() => setIsOpen(false)} isDisabled={isPublishing}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
